import _ from "lodash";
import {
  FC,
  ReactElement,
  ReactNode,
  useCallback,
  useMemo,
  useState,
} from "react";
import { Alert, Col, Form, Row } from "react-bootstrap";
import Dropzone, { FileRejection } from "react-dropzone";
import { toast } from "react-toastify";
import { v4 as uuid } from "uuid";
import useLoading from "../../../../../shared/src/hooks/useLoading";
import useCaseAttachmentsByCaseId from "../../../../modules/filestore/hooks/useCaseAttachmentsByCaseId";
import useCaseAttachmentsPermissionsByCaseId from "../../../../modules/filestore/hooks/useCaseAttachmentsPermissionsByCaseId";
import { EUFinanceFileStoreSaveFileMetadata } from "../../../../modules/filestore/models/metadata/EUFinanceFilestoreSaveFileMetadata";
import { useEUFinanceFileStoreService } from "../../../../modules/filestore/services/EUFinanceFileStoreService";
import "./styles.scss";

const CLASSNAME =
  "eu-finance-case-attachment-uploader__case-attachment-dropzone";
const MAX_FILE_SIZE = 35000000; // 35 MB

type DropzoneAlert = {
  key: string;
  variant: string;
  message: ReactElement;
};

export type CaseAttachmentDropzoneProps = {
  caseId: string;
  caseNumber: string;
  label: string;
  description?: ReactNode;
  allowedExtensions: Record<string, string[]> | undefined;
  required?: boolean;
  onAfterUploadSuccess?: (params?: unknown) => void;
};

const buildSaveMetadata = (
  caseId: string,
  caseNumber: string
): EUFinanceFileStoreSaveFileMetadata => {
  return { caseId, caseNumber };
};

const CaseAttachmentDropzone: FC<CaseAttachmentDropzoneProps> = ({
  caseId,
  caseNumber,
  label,
  description,
  allowedExtensions,
  required = false,
  onAfterUploadSuccess,
}): ReactElement => {
  const fileStoreService = useEUFinanceFileStoreService();

  const loading = useLoading();
  const [fileRejectionAlerts, setFileRejectionAlerts] = useState<
    DropzoneAlert[]
  >([]);

  const { data: caseAttachmentsPermissions } =
    useCaseAttachmentsPermissionsByCaseId(caseId);

  const { refreshFiles, component: documentsComponent } =
    useCaseAttachmentsByCaseId(caseId);

  const saveFiles = useCallback(
    async (filesToSave: File[]) => {
      return loading(async () => {
        try {
          const responses = await Promise.all(
            filesToSave.map((file) => {
              const metadata = buildSaveMetadata(caseId, caseNumber);
              return fileStoreService.saveFileByKeyAndMetadata(
                caseId,
                file,
                metadata
              );
            })
          );
          if (
            responses
              .flat()
              .every((response) => response?.responseCode === "201")
          ) {
            toast.success(`Successfully uploaded ${responses.length} file(s)`);
            await refreshFiles();
            onAfterUploadSuccess?.();
          } else {
            toast.error("Failed to upload some files", {
              autoClose: false,
            });
          }
        } catch (error) {
          toast.error("An error occurred while uploading file(s)");
        }
      });
    },
    [
      loading,
      caseId,
      caseNumber,
      fileStoreService,
      refreshFiles,
      onAfterUploadSuccess,
    ]
  );

  const onDrop = useCallback(
    async (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      saveFiles(acceptedFiles);
      setFileRejectionAlerts((previousFileRejectionAlerts) => [
        ...previousFileRejectionAlerts,
        ...fileRejections.flatMap(({ errors, file }) => {
          return errors.map(({ message }) => ({
            key: uuid(),
            variant: "danger",
            message: (
              <>
                <strong>{file.name}</strong> - {message}
              </>
            ),
          }));
        }),
      ]);
    },
    [saveFiles]
  );

  const fileExtensions = useMemo(
    () =>
      _.sortBy(_.uniq(Object.values(allowedExtensions ?? {}).flat())).join(
        ", "
      ),
    [allowedExtensions]
  );

  return (
    <div className={CLASSNAME}>
      <Row>
        <Col>
          <Form.Label className="fw-bold">
            {label}
            {required && <span className="text-danger">*</span>}
          </Form.Label>
        </Col>
      </Row>
      {description}
      <Row>
        <Col>
          {fileRejectionAlerts.map(({ key, variant, message }) => (
            <Alert
              key={key}
              variant={variant}
              dismissible
              onClose={() => {
                setFileRejectionAlerts((previousFileRejectionAlerts) =>
                  previousFileRejectionAlerts.filter(
                    (alert) => alert.key !== key
                  )
                );
              }}
            >
              {message}
            </Alert>
          ))}
        </Col>
      </Row>
      {caseAttachmentsPermissions?.canUploadCaseAttachments && (
        <Row>
          <Col>
            <Dropzone
              onDrop={onDrop}
              accept={allowedExtensions}
              maxSize={MAX_FILE_SIZE}
            >
              {({ getRootProps, getInputProps }) => (
                <div
                  {...getRootProps({
                    className: "dropzone",
                  })}
                >
                  <input {...getInputProps()} />
                  <p>Drop file(s) here or click to select file(s)</p>
                  <p>Only {fileExtensions} files can be uploaded</p>
                </div>
              )}
            </Dropzone>
          </Col>
        </Row>
      )}
      {documentsComponent}
    </div>
  );
};

CaseAttachmentDropzone.defaultProps = {
  description: undefined,
  required: false,
  onAfterUploadSuccess: undefined,
};

export default CaseAttachmentDropzone;
